import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

export type ToastType = 'success' | 'danger' | 'warning' | 'info';

interface ToastItem {
  id: number;
  type: ToastType;
  message: string;
}

interface ToastOptions {
  // milisegundos; 0 lo deja fijo hasta que se cierre a mano
  duration?: number;
}

interface ToastApi {
  show: (type: ToastType, message: string, options?: ToastOptions) => void;
  success: (message: string, options?: ToastOptions) => void;
  error: (message: string, options?: ToastOptions) => void;
  warning: (message: string, options?: ToastOptions) => void;
  info: (message: string, options?: ToastOptions) => void;
}

const ICONS: Record<ToastType, string> = {
  success: 'fa-solid fa-circle-check',
  danger: 'fa-solid fa-circle-xmark',
  warning: 'fa-solid fa-triangle-exclamation',
  info: 'fa-solid fa-circle-info',
};

const DEFAULT_DURATION = 5000;

const noop = () => undefined;
const ToastContext = createContext<ToastApi>({
  show: noop,
  success: noop,
  error: noop,
  warning: noop,
  info: noop,
});

/**
 * Toasts compartidos. Envolver la app con <ToastProvider> y en cualquier
 * componente: `const toast = useToast(); toast.warning('Mensaje');`
 */
export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback(
    (type: ToastType, message: string, options: ToastOptions = {}) => {
      const id = nextId.current++;
      const { duration = DEFAULT_DURATION } = options;
      setToasts((prev) => [...prev.slice(-3), { id, type, message }]);
      if (duration > 0) {
        setTimeout(() => dismiss(id), duration);
      }
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (message, options) => show('success', message, options),
      error: (message, options) => show('danger', message, options),
      warning: (message, options) => show('warning', message, options),
      info: (message, options) => show('info', message, options),
    }),
    [show],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        aria-live="polite"
        style={{
          position: 'fixed',
          top: '1rem',
          right: '1rem',
          zIndex: 2000,
          width: 'min(360px, calc(100vw - 2rem))',
        }}
      >
        {toasts.map(({ id, type, message }) => (
          <div
            key={id}
            role="alert"
            className={`alert alert-${type} alert-dismissible shadow mb-2 d-flex align-items-start`}
          >
            <i className={`${ICONS[type]} mr-3 mt-1`} />
            <span className="flex-grow-1 pr-3">{message}</span>
            <button
              type="button"
              className="close"
              aria-label="Cerrar"
              onClick={() => dismiss(id)}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => useContext(ToastContext);
